"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Cpu, FlaskConical, Layers } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface SolutionItem {
  id: number;
  title: string;
  category: string;
  description: string;
  link: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
  keyFeature1: string;
  keyFeature2: string;
}
const solutionsData: SolutionItem[] = [{
  id: 1,
  title: "Raw Material Identification",
  category: "Warehouse & QC",
  description: "Rapid, non-destructive chemical verification through packaging to streamline incoming raw material clearance and maintain strict compliance.",
  link: "/RawMaterialIdentificationPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_identification.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_identification.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_identification.webp-wvc-sizes",
  imageAlt: "Technician performing handheld spectroscopic verification of raw materials in a warehouse setting",
  keyFeature1: "Through-container Raman and NIR analysis",
  keyFeature2: "Incoming container testing capability"
}, {
  id: 2,
  title: "Powder Testing",
  category: "Formulation & Processing",
  description: "Comprehensive characterisation of powder flowability, shear properties, and compressibility to prevent manufacturing bottlenecks.",
  link: "/PowderTestingPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_lab.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_lab.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_lab.png-wvc-sizes",
  imageAlt: "Laboratory powder rheometer and density measurement instruments on a bench",
  keyFeature1: "Dynamic and shear powder testing",
  keyFeature2: "Direct formulation to process scaling"
}, {
  id: 3,
  title: "Process Analytical Technology",
  category: "In-line & At-line Monitoring",
  description: "Real-time process monitoring solutions for drying endpoints, blending uniformity, and continuous chemical manufacturing.",
  link: "/ProcessAnalyticalTechnologyPage",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_analytical_technology.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_analytical_technology.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_analytical_technology.webp-wvc-sizes",
  imageAlt: "Industrial spectroscopic probe installed directly on a pharmaceutical process vessel",
  keyFeature1: "Real-time blend and drying feedback",
  keyFeature2: "Fully compliant GMP data interfaces"
}];
export default function Solutions() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section id="relatedsolutions" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3">
            Related Applications
          </Badge>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground mb-4">
            Explore Targeted Analytical Solutions
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Discover tailored measurement systems and qualification services designed to meet regulatory standards across Ireland and the UK.
          </p>
        </div>

        {/* Solutions Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {solutionsData.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full">
              <Card className="h-full flex flex-col justify-between overflow-hidden border border-border shadow-sm hover:shadow-md transition-shadow">
                <div className="relative aspect-16/9 w-full overflow-hidden bg-muted">
                  <img src={item.imageSrc} data-wvc-srcset={item.imageSrcset} data-wvc-sizes={item.imageSizes} alt={item.imageAlt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
                  <div className="absolute top-3 left-3">
                    <Badge variant="default" className="bg-primary text-primary-foreground shadow-xs">
                      {item.category}
                    </Badge>
                  </div>
                </div>

                <CardHeader className="pt-6 pb-3">
                  <CardTitle className="text-xl font-semibold text-foreground">
                    {item.title}
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground line-clamp-3 mt-2">
                    {item.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="py-2">
                  <ul className="space-y-2 border-t border-border pt-4 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="size-4 text-primary shrink-0" />
                      <span>{item.keyFeature1}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="size-4 text-primary shrink-0" />
                      <span>{item.keyFeature2}</span>
                    </li>
                  </ul>
                </CardContent>

                <CardFooter className="pt-4 pb-6">
                  <Button asChild variant="outline" className="w-full justify-between group">
                    <Link to={item.link}>
                      <span>View Solution</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Global CTA Link */}
        <div className="mt-12 text-center">
          <Button asChild variant="secondary" size="lg">
            <Link to="/SolutionsPage">
              <span>View All Analytical Solutions</span>
              <ArrowRight className="size-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}